// frontend/src/components/Product/ProductReactions.jsx
import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useAuth } from "../../context/AuthContext";
import { addReaction, getUserReaction, getReactionCounts } from "../../api";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faThumbsUp,
  faThumbsDown,
  faHeart,
  faCheckCircle,
  faTimesCircle,
  faSpinner,
} from "@fortawesome/free-solid-svg-icons";

// ============================================
// BRAND CONSTANTS
// ============================================
const BRAND = {
  primary: "#006B2E",
  secondary: "#D9A321",
  goldHover: "#B8860B",
  forest: "#014421",
  charcoal: "#1F1F1F",
  slate: "#5A5A5A",
  white: "#FFFFFF",
  lightBg: "#F8F8F8",
};

// ============================================
// REACTION CONFIG
// ============================================
const REACTIONS = [
  {
    type: "like",
    icon: faThumbsUp,
    label: "Like",
    color: "#3b82f6",
    bgColor: "#dbeafe",
  },
  {
    type: "dislike",
    icon: faThumbsDown,
    label: "Dislike",
    color: "#ef4444",
    bgColor: "#fee2e2",
  },
  {
    type: "love",
    icon: faHeart,
    label: "Love",
    color: "#ec4899",
    bgColor: "#fce7f3",
  },
  {
    type: "recommend",
    icon: faCheckCircle,
    label: "Recommend",
    color: "#10b981",
    bgColor: "#d1fae5",
  },
  {
    type: "dont_recommend",
    icon: faTimesCircle,
    label: "Don't Recommend",
    color: "#f59e0b",
    bgColor: "#fef3c7",
  },
];

// ============================================
// MAIN COMPONENT
// ============================================
const ProductReactions = ({ productId, onReactionChange }) => {
  const { user } = useAuth();
  const [reactionCounts, setReactionCounts] = useState({
    like: 0,
    dislike: 0,
    love: 0,
    recommend: 0,
    dont_recommend: 0,
  });
  const [userReaction, setUserReaction] = useState(null);
  const [loading, setLoading] = useState(false);
  const [totalReactions, setTotalReactions] = useState(0);

  // ============================================
  // FETCH REACTION DATA
  // ============================================
  useEffect(() => {
    fetchReactionData();
  }, [productId]);

  const fetchReactionData = async () => {
    try {
      const [countsRes, userReactionRes] = await Promise.all([
        getReactionCounts(productId),
        user
          ? getUserReaction(productId)
          : Promise.resolve({ data: { reaction: null } }),
      ]);
      setReactionCounts(countsRes.data.counts);
      setUserReaction(userReactionRes.data.reaction);

      const total = Object.values(countsRes.data.counts).reduce(
        (sum, val) => sum + val,
        0,
      );
      setTotalReactions(total);
    } catch (error) {
      console.error("Error fetching reaction data:", error);
    }
  };

  // ============================================
  // HANDLE REACTION
  // ============================================
  const handleReaction = async (reactionType) => {
    if (!user) {
      alert("Please login to react to products");
      return;
    }

    setLoading(true);
    try {
      const { data } = await addReaction(productId, reactionType);
      setReactionCounts(data.counts);
      setUserReaction(data.action === "removed" ? null : reactionType);

      const total = Object.values(data.counts).reduce(
        (sum, val) => sum + val,
        0,
      );
      setTotalReactions(total);

      if (onReactionChange) onReactionChange(data.counts);
    } catch (error) {
      console.error("Error adding reaction:", error);
      alert(error.response?.data?.message || "Failed to add reaction");
    } finally {
      setLoading(false);
    }
  };

  // ============================================
  // RENDER
  // ============================================
  return (
    <motion.div
      className="product-reactions"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
    >
      <div className="reactions-header">
        <h4>What do you think?</h4>
        {totalReactions > 0 && (
          <span className="total-reactions">{totalReactions} reactions</span>
        )}
      </div>

      <div className="reactions-grid">
        {REACTIONS.map((reaction) => {
          const isActive = userReaction === reaction.type;
          const count = reactionCounts[reaction.type] || 0;

          return (
            <motion.button
              key={reaction.type}
              className={`reaction-btn ${isActive ? "active" : ""}`}
              onClick={() => handleReaction(reaction.type)}
              disabled={loading}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              style={{
                borderColor: isActive ? reaction.color : "#e5e5e5",
                background: isActive ? reaction.bgColor : "transparent",
              }}
            >
              <FontAwesomeIcon
                icon={reaction.icon}
                style={{ color: isActive ? reaction.color : "#5A5A5A" }}
              />
              <span className="reaction-label">{reaction.label}</span>
              {count > 0 && <span className="reaction-count">{count}</span>}
              {loading && (
                <FontAwesomeIcon
                  icon={faSpinner}
                  spin
                  className="loading-spinner"
                />
              )}
            </motion.button>
          );
        })}
      </div>

      {/* ============================================
          STYLES
          ============================================ */}
      <style>{`
        .product-reactions {
          margin: 1.5rem 0;
          padding: 1rem 0;
          border-top: 1px solid #f0f0f0;
          border-bottom: 1px solid #f0f0f0;
        }

        .reactions-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 0.75rem;
        }

        .reactions-header h4 {
          font-size: 0.875rem;
          color: #1F1F1F;
          margin: 0;
        }

        .total-reactions {
          font-size: 0.7rem;
          color: #5A5A5A;
          background: #F8F8F8;
          padding: 0.1rem 0.5rem;
          border-radius: 12px;
        }

        .reactions-grid {
          display: flex;
          flex-wrap: wrap;
          gap: 0.5rem;
        }

        .reaction-btn {
          display: flex;
          align-items: center;
          gap: 0.4rem;
          padding: 0.3rem 0.8rem;
          border: 2px solid #e5e5e5;
          border-radius: 20px;
          background: transparent;
          cursor: pointer;
          transition: all 0.3s ease;
          font-size: 0.8rem;
          font-family: "Inter", sans-serif;
          font-weight: 500;
          color: #1F1F1F;
          position: relative;
          min-height: 36px;
        }

        .reaction-btn:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
        }

        .reaction-btn.active {
          border-color: var(--reaction-color, #D9A321);
        }

        .reaction-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .reaction-label {
          font-size: 0.7rem;
        }

        .reaction-count {
          font-size: 0.65rem;
          font-weight: 600;
          background: #F8F8F8;
          padding: 0.05rem 0.3rem;
          border-radius: 8px;
          color: #5A5A5A;
        }

        .reaction-btn.active .reaction-count {
          background: rgba(0, 0, 0, 0.05);
        }

        .loading-spinner {
          font-size: 0.6rem;
          margin-left: 0.15rem;
        }

        /* ========== RESPONSIVE ========== */
        @media (max-width: 480px) {
          .reactions-grid {
            gap: 0.35rem;
          }

          .reaction-btn {
            padding: 0.2rem 0.5rem;
            font-size: 0.7rem;
            min-height: 30px;
          }

          .reaction-label {
            font-size: 0.6rem;
          }

          .reaction-count {
            font-size: 0.55rem;
            padding: 0.05rem 0.2rem;
          }

          .reactions-header {
            flex-direction: column;
            align-items: flex-start;
            gap: 0.25rem;
          }
        }
      `}</style>
    </motion.div>
  );
};

export default ProductReactions;
